import { useCallback, useState, useEffect, useMemo } from 'react';
import { useApiData, useAuthenticatedFetch } from '../client';
import type { MediaSimRole, MediaSimUserRecord, UserInfo } from '../../types/media-sim';

function useDebouncedValue(value: string, delayMs: number): string {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(timer);
  }, [value, delayMs]);
  return debounced;
}

export function useUsers(search?: string) {
  const debounced = useDebouncedValue(search || '', 250);
  const endpoint = useMemo(() => {
    if (debounced.length < 2) return '';
    const params = new URLSearchParams({ search: debounced, limit: '20' });
    return `/media-sim/users/?${params}`;
  }, [debounced]);
  const { data, loading, error } = useApiData<UserInfo[]>(endpoint);
  // Return empty array when not searching instead of stale/error data
  return { data: endpoint ? data : null, loading: endpoint ? loading : false, error: endpoint ? error : null };
}

/**
 * Upsert the current user into the mediasim_users table.
 *
 * Called once after authentication so the user appears in the directory and
 * their role (standard/admin) is established. Idempotent server-side.
 */
export function useRegisterUser() {
  const { fetchData } = useAuthenticatedFetch<MediaSimUserRecord>();
  const registerUser = useCallback(
    (payload?: { email?: string; name?: string }) =>
      fetchData('/media-sim/users/register/', {
        method: 'POST',
        body: JSON.stringify(payload ?? {}),
      }),
    [fetchData]
  );
  return { registerUser };
}

/** The current user's mediasim_users row (includes role). */
export function useCurrentMediaSimUser() {
  return useApiData<MediaSimUserRecord>('/media-sim/users/me/');
}

/** Full user list with roles. Admin-only endpoint. */
export function useAllUsers() {
  return useApiData<MediaSimUserRecord[]>('/media-sim/users/all/');
}

/** Set a user's role (standard/admin). Admin-only. */
export function useUpdateUserRole() {
  const { fetchData } = useAuthenticatedFetch<MediaSimUserRecord>();
  const updateRole = useCallback(
    (userId: string, role: MediaSimRole) =>
      fetchData(`/media-sim/users/${userId}/`, {
        method: 'PATCH',
        body: JSON.stringify({ role }),
      }),
    [fetchData]
  );
  return { updateRole };
}
